import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

export interface EmploymentAgreementData {
  companyName: string;
  companyOrgNo: string;
  companyAddress: string;
  workerName: string;
  workerSsn: string;
  workerAddress: string;
  workerEmail: string;
  workerPhone: string;
  roleTitle: string;
  startDate: string;
  endDate?: string;
  employmentType: string;
  workPercentage: string;
  salary: string;
  salaryType: string;
  vacationDays: string;
  noticeDays: string;
  workPlace?: string;
  collectiveAgreement?: string | null;
  otherTerms?: string;
}

export const generateEmploymentAgreementPdf = async (
  data: EmploymentAgreementData,
): Promise<Uint8Array> => {
  const pdfDoc = await PDFDocument.create();
  const page = pdfDoc.addPage([595.28, 841.89]); // A4 Size
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  // Modern Color Palette
  const navyColor = rgb(16 / 255, 34 / 255, 64 / 255);
  const darkGray = rgb(60 / 255, 66 / 255, 74 / 255);
  const lightGrayBg = rgb(248 / 255, 249 / 255, 250 / 255);
  const lightBorder = rgb(222 / 255, 226 / 255, 230 / 255);
  const bankidGreenBg = rgb(240 / 255, 248 / 255, 240 / 255);
  const bankidGreenBorder = rgb(200 / 255, 230 / 255, 200 / 255);

  const drawText = (
    text: string,
    x: number,
    y: number,
    size = 10,
    isBold = false,
    color = darkGray,
  ) => {
    page.drawText(text, {
      x,
      y,
      size,
      font: isBold ? boldFont : font,
      color,
    });
  };

  const drawSectionHeader = (title: string, y: number) => {
    drawText(title, 54, y, 11, true, navyColor);
    page.drawLine({
      start: { x: 54, y: y - 6 },
      end: { x: 541, y: y - 6 },
      thickness: 1,
      color: lightBorder,
    });
  };

  const drawWrappedText = (
    text: string,
    x: number,
    startY: number,
    width: number,
    size = 9,
  ): number => {
    const lines = text.split("\n");
    let currentY = startY;

    for (const paragraph of lines) {
      const words = paragraph.split(" ");
      let line = "";

      for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + " ";
        const textWidth = font.widthOfTextAtSize(testLine, size);
        if (textWidth > width && n > 0) {
          page.drawText(line, {
            x,
            y: currentY,
            size,
            font,
            color: darkGray,
          });
          line = words[n] + " ";
          currentY -= size + 4;
        } else {
          line = testLine;
        }
      }
      page.drawText(line, {
        x,
        y: currentY,
        size,
        font,
        color: darkGray,
      });
      currentY -= size + 6;
    }
    return currentY;
  };

  // 1. Top Decorative Brand Bar
  page.drawRectangle({
    x: 54,
    y: 795,
    width: 487,
    height: 6,
    color: navyColor,
  });

  // 2. Title & Subtitle
  drawText("ANSTÄLLNINGSAVTAL", 54, 765, 18, true, navyColor);
  drawText(
    "Upprättat digitalt enligt gällande villkor och överenskommelser",
    54,
    750,
    9,
    false,
    rgb(120 / 255, 126 / 255, 135 / 255),
  );

  // 3. Section 1: Parter (Parties)
  drawSectionHeader("1. PARTER", 720);

  // Employer Card (Left Column Box)
  page.drawRectangle({
    x: 54,
    y: 590,
    width: 235,
    height: 110,
    color: lightGrayBg,
    borderColor: lightBorder,
    borderWidth: 1,
  });
  drawText("Arbetsgivare", 64, 685, 10, true, navyColor);
  drawText(`Företag: ${data.companyName || "-"}`, 64, 668, 9);
  drawText(`Org.nr: ${data.companyOrgNo || "-"}`, 64, 652, 9);
  drawText(`Adress: ${data.companyAddress || "-"}`, 64, 636, 9);

  // Employee Card (Right Column Box)
  page.drawRectangle({
    x: 306,
    y: 590,
    width: 235,
    height: 110,
    color: lightGrayBg,
    borderColor: lightBorder,
    borderWidth: 1,
  });
  drawText("Arbetstagare", 316, 685, 10, true, navyColor);
  drawText(`Namn: ${data.workerName || "-"}`, 316, 668, 9);
  drawText(`Personnr: ${data.workerSsn || "-"}`, 316, 652, 9);
  drawText(`Adress: ${data.workerAddress || "-"}`, 316, 636, 9);
  drawText(`E-post: ${data.workerEmail || "-"}`, 316, 620, 9);
  drawText(`Tel: ${data.workerPhone || "-"}`, 316, 604, 9);

  // 4. Section 2: Anställningsdetaljer
  drawSectionHeader("2. ANSTÄLLNING", 565);

  const startDetailsY = 540;
  // Left Col Details
  drawText("Befattning / Roll:", 54, startDetailsY, 9, true, navyColor);
  drawText(data.roleTitle || "-", 170, startDetailsY, 9);

  drawText("Anställningsform:", 54, startDetailsY - 18, 9, true, navyColor);
  drawText(data.employmentType || "-", 170, startDetailsY - 18, 9);

  drawText("Tillträdesdag:", 54, startDetailsY - 36, 9, true, navyColor);
  drawText(data.startDate || "-", 170, startDetailsY - 36, 9);

  // Right Col Details
  drawText("Sysselsättningsgrad:", 306, startDetailsY, 9, true, navyColor);
  drawText(data.workPercentage || "-", 420, startDetailsY, 9);

  if (data.endDate) {
    drawText("Slutdatum:", 306, startDetailsY - 18, 9, true, navyColor);
    drawText(data.endDate, 420, startDetailsY - 18, 9);
  }

  // Arbetsplats drawn as full width on line 4
  let workPlaceVal = data.workPlace || "-";
  if (data.workPlace) {
    const parts = data.workPlace.split(",");
    if (parts.length > 2) {
      workPlaceVal = parts.slice(0, 2).join(",").trim();
    }
  }
  drawText("Arbetsplats:", 54, startDetailsY - 54, 9, true, navyColor);
  drawText(workPlaceVal, 170, startDetailsY - 54, 9);

  // 5. Section 3: Lön
  drawSectionHeader("3. LÖN", 457);

  const startSalaryY = 432;
  const isHourly =
    data.salaryType === "Hourly" ||
    data.salaryType === "Timlön" ||
    (data.employmentType !== undefined &&
      (data.employmentType.toLowerCase().includes("tim") ||
        data.employmentType.toLowerCase().includes("behov") ||
        data.employmentType === "ExclusiveTemporaryWorker"));
  const salaryLabel = isHourly ? "Timlön:" : "Månadslön:";
  let salaryVal = data.salary || "-";
  if (data.salary && !data.salary.toLowerCase().includes("kr")) {
    salaryVal = `${data.salary} kr`;
  }

  // Left Column
  drawText("Löneform:", 54, startSalaryY, 9, true, navyColor);
  drawText(data.salaryType || "-", 170, startSalaryY, 9);

  drawText(salaryLabel, 54, startSalaryY - 18, 9, true, navyColor);
  drawText(salaryVal, 170, startSalaryY - 18, 9);

  // Right Column
  drawText("Löneutbetalning:", 306, startSalaryY, 9, true, navyColor);
  drawText("Månadsvis i efterskott", 420, startSalaryY, 9);

  // 6. Section 4: Villkor
  drawSectionHeader("4. VILLKOR", 385);

  const startTermsY = 360;
  const semesterStr = isHourly
    ? "Semesterersättning utbetalas enligt semesterlagen."
    : `${data.vacationDays || "-"} dagar/år`;

  const collectiveAgreementStr = data.collectiveAgreement
    ? `Ja (${data.collectiveAgreement})`
    : "Nej";

  // Listed as single lines to prevent horizontal overlap
  drawText("Arbetstid:", 54, startTermsY, 9, true, navyColor);
  drawText("Enligt schema / överenskommelse", 170, startTermsY, 9);

  drawText("Semester:", 54, startTermsY - 18, 9, true, navyColor);
  drawText(semesterStr, 170, startTermsY - 18, 9);

  drawText("Uppsägningstid:", 54, startTermsY - 36, 9, true, navyColor);
  drawText(data.noticeDays || "-", 170, startTermsY - 36, 9);

  drawText("Kollektivavtal:", 54, startTermsY - 54, 9, true, navyColor);
  drawText(collectiveAgreementStr, 170, startTermsY - 54, 9);

  // 7. Section 5: Övriga villkor
  let nextY = 275;
  if (data.otherTerms) {
    drawSectionHeader("5. ÖVRIGA VILLKOR", 285);
    nextY = drawWrappedText(data.otherTerms, 54, 260, 487, 9) - 10;
  }

  // 8. Section 6: Signering Banner (Bottom)
  const bannerY = Math.min(nextY - 20, 180);
  page.drawRectangle({
    x: 54,
    y: bannerY - 50,
    width: 487,
    height: 60,
    color: bankidGreenBg,
    borderColor: bankidGreenBorder,
    borderWidth: 1,
  });

  drawText(
    "6. ELEKTRONISK SIGNERING",
    64,
    bannerY - 8,
    10,
    true,
    rgb(46 / 255, 117 / 255, 46 / 255),
  );
  drawText(
    "Detta avtal har upprättats digitalt. Parterna signerar avtalet elektroniskt via svenskt BankID.",
    64,
    bannerY - 24,
    9,
  );
  drawText(
    "Fullständiga elektroniska verifikationssignaturer bifogas detta dokument efter slutförande.",
    64,
    bannerY - 38,
    9,
  );

  return await pdfDoc.save();
};
